import { useNavigate } from "react-router-dom";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import { Fragment, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  ALL_EMPLOYEE_CLOCK_INS_BY_AGENCY_ID,
  EMPLOYEE_ACTIVE_STATS,
  EMPLOYEE_LEAVE_SUMMARY,
  EXPORT_ATTTENDANCE_TO_EXCEL,
  HOLIDAYS,
  PODS_DATA_ATTENDANCE,
} from "../../api/query";
import { useAuth } from "../../context/AuthContext";
import Loader from "../../components/common/Loader";
import { endOfMonth, format, startOfMonth } from "date-fns";

interface AttendanceRecord {
  record_date: string;
  week_day: string;
  check_in_time: string | null;
  check_out_time: string | null;
  is_present: boolean;
  is_leave: boolean;
  is_holiday: boolean;
  is_absent: boolean;
  is_weekend: boolean;
  is_extra_day: boolean;
  is_half_day: boolean;
  is_night_shift?: boolean;
  hours?: number;
  minutes?: number;
}

interface MeetingItem {
  meeting_id: number;
  title: string;
  time: string;
  status: "Present" | "Absent";
  pod_id: number | null;
  pod_name: string | null;
  meeting_type_name: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
}

interface DailyMeeting {
  date: string; // YYYY-MM-DD
  total_meetings: number;
  meetings: MeetingItem[];
}

// Ek row = ek pod ya ek meeting type
interface MeetingGroup {
  key: string;
  label: string;
  isPod: boolean;
  byDay: Map<number, MeetingItem[]>;
}

interface AggregatedDayRecord {
  record_date: string;
  week_day: string;
  is_present: boolean;
  is_leave: boolean;
  is_holiday: boolean;
  is_absent: boolean;
  is_weekend: boolean;
  is_extra_day: boolean;
  is_night_shift: boolean;
  is_half_day: boolean;
  total_minutes: number;
  records: AttendanceRecord[];
}

interface EmployeeRecord {
  employee_id: number;
  employee_name: string;
  attendance: AttendanceRecord[];
}

// Leave summary API (approved leaves only + date_of_joining)
interface LeaveRecord {
  id: number;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  leave_type: string;
  is_half_day: boolean;
  pay_type: "Paid" | "Unpaid" | "Partial";
}

interface LeaveSummaryRecord {
  employee_id: number;
  employee_name: string;
  date_of_joining: string | null; // YYYY-MM-DD
  leaves: LeaveRecord[];
}

const LEAVE_PER_MONTH = 1.5;
const JOINING_CUTOFF_DAY = 15; // joining day < 15 => joining month ki leave milegi

type AttendanceSymbol = "P" | "A" | "L" | "HD" | "W" | "H" | "ED" | "NS" | "-";

// "2026-09-10" / "2026-09-10T00:00:00" -> [2026, 9, 10] (timezone issue se bachne ke liye manual parse)
const parseYMD = (value?: string | null): [number, number, number] | null => {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
};

// "YYYY-MM-DD" -> comparable number (timezone issue nahi aata)
const toKey = (y: number, m: number, d: number) => y * 10000 + m * 100 + d;

// 1.5 -> "1.5", 2 -> "2"
const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

interface LeaveBreakdown {
  paid: number;
  unpaid: number;
  total: number;
  paidFull: number; // full day leave ka paid hissa
  paidHalf: number; // half day leave ka paid hissa
}

const getLeaveBreakdownInMonth = (
  leaves: LeaveRecord[],
  y: number,
  m: number,
): LeaveBreakdown => {
  const result: LeaveBreakdown = {
    paid: 0,
    unpaid: 0,
    total: 0,
    paidFull: 0,
    paidHalf: 0,
  };

  leaves.forEach((leave) => {
    const start = parseYMD(leave.start_date);
    const end = parseYMD(leave.end_date);

    if (!start || !end) return;

    const cur = new Date(Date.UTC(start[0], start[1] - 1, start[2]));
    const last = new Date(Date.UTC(end[0], end[1] - 1, end[2]));

    while (cur <= last) {
      if (cur.getUTCFullYear() === y && cur.getUTCMonth() + 1 === m) {
        const days = leave.is_half_day ? 0.5 : 1;

        result.total += days;

        let paidPortion = 0;
        let unpaidPortion = 0;

        if (leave.pay_type === "Paid") {
          paidPortion = days;
        } else if (leave.pay_type === "Unpaid") {
          unpaidPortion = days;
        } else if (leave.pay_type === "Partial") {
          // Partial = 50% paid + 50% unpaid
          paidPortion = days * 0.5;
          unpaidPortion = days * 0.5;
        }

        result.paid += paidPortion;
        result.unpaid += unpaidPortion;

        if (leave.is_half_day) {
          result.paidHalf += paidPortion;
        } else {
          result.paidFull += paidPortion;
        }
      }

      cur.setUTCDate(cur.getUTCDate() + 1);
    }
  });

  return result;
};

const AllAttendanceNew = () => {
  const navigate = useNavigate();

  const { userInfo } = useAuth();
  const agencyID = userInfo?.agencies?.id || "";
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
    null,
  );

  const daysInMonth = new Date(year, month, 0).getDate();

  //api
  const [getAttendance, { data, loading }] = useLazyQuery(
    ALL_EMPLOYEE_CLOCK_INS_BY_AGENCY_ID,
    {
      fetchPolicy: "no-cache",
    },
  );

  // Single call: sabhi employees ka joining date + approved leaves
  const [getLeaveSummary, { data: leaveSummaryData }] = useLazyQuery(
    EMPLOYEE_LEAVE_SUMMARY,
    {
      fetchPolicy: "no-cache",
    },
  );

  const leaveSummaryMap = new Map<number, LeaveSummaryRecord>();
  leaveSummaryData?.employee_leave_summary?.records?.forEach(
    (r: LeaveSummaryRecord) => leaveSummaryMap.set(Number(r.employee_id), r),
  );

  const [
    getEmployeeActiveStats,
    { data: activeStatsData, loading: activeStatsLoading },
  ] = useLazyQuery(EMPLOYEE_ACTIVE_STATS, {
    fetchPolicy: "no-cache",
  });

  const [exportAttendance, { loading: exportLoading }] = useLazyQuery(
    EXPORT_ATTTENDANCE_TO_EXCEL,
    {
      fetchPolicy: "no-cache",
    },
  );

  const [getHolidays, { data: holidayData, loading: holidayLoading }] =
    useLazyQuery(HOLIDAYS, {
      fetchPolicy: "no-cache",
    });

  // Pods + members (employee kis pod ka member hai)
  const [getPods, { data: podsData }] = useLazyQuery(PODS_DATA_ATTENDANCE, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (agencyID && month && year) {
      const firstDayOfMonth = startOfMonth(new Date(year, month - 1));
      const lastDayOfMonth = endOfMonth(new Date(year, month - 1));

      const formattedStartDate = format(firstDayOfMonth, "yyyy-MM-dd");
      const formattedEndDate = format(lastDayOfMonth, "yyyy-MM-dd");

      getAttendance({
        variables: {
          agency_id: parseInt(agencyID),
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        },
      });

      // Joining date + approved leaves (1 Jan se selected month end tak) - ek hi call
      getLeaveSummary({
        variables: {
          agency_id: parseInt(agencyID),
          start_date: `${year}-01-01`,
          end_date: formattedEndDate,
        },
      });

      // Get active/open goals and tasks for each employee
      getEmployeeActiveStats({
        variables: {
          target_date: formattedEndDate,
        },
      });

      // Pods and their members
      getPods({
        variables: {
          agency_id: parseInt(agencyID),
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        },
      });

      // Holidays
      getHolidays({
        variables: {
          start_date: `${formattedStartDate}T00:00:00`,
          end_date: `${formattedEndDate}T23:59:59`,
        },
      });
    }
  }, [agencyID, month, year]);

  const holidays = holidayData?.holidays?.holidays || [];

  useEffect(() => {
    if (data && data.all_employee_clock_ins_by_agency_id?.status_code == 200) {
      setEmployees(data.all_employee_clock_ins_by_agency_id.records || []);
    }
  }, [data]);

  // ---------- Leave helpers ----------

  // Joining date se is year me leave kis month se milegi
  // 1..12 = us month se, 13 = is year me koi leave nahi
  const getLeaveStartMonth = (doj?: string | null): number => {
    const parsed = parseYMD(doj);
    if (!parsed) return 1;
    const [y, m, d] = parsed;
    if (y < year) return 1;
    if (y > year) return 13;
    return d < JOINING_CUTOFF_DAY ? m : m + 1;
  };

  const getPreviousLeaveBalance = (
    leaves: LeaveRecord[],
    startMonth: number,
  ): number => {
    let balance = 0;

    for (let m = startMonth; m < month; m++) {
      balance += LEAVE_PER_MONTH;

      const breakdown = getLeaveBreakdownInMonth(leaves, year, m);

      balance -= breakdown.paid;
    }

    return balance;
  };

  // ---------- Attendance helpers ----------

  // Map flags → status letter for aggregated records
  const getStatus = (a: AggregatedDayRecord): AttendanceSymbol => {
    if (a.is_extra_day) return "ED";
    if (a.is_night_shift) return "NS";
    if (a.is_half_day) return "HD";
    if (a.is_present) return "P";
    if (a.is_leave) return "L";
    if (a.is_holiday) return "H";
    if (a.is_weekend) return "W";
    if (a.is_absent) return "A";
    return "A";
  };

  // Check if a date is in the future
  const isFutureDate = (day: number): boolean => {
    const currentDate = new Date();
    const checkDate = new Date(year, month - 1, day);
    return checkDate > currentDate;
  };

  // Kya ye din holidays list me hai?
  const isHolidayDate = (day: number): boolean => {
    const target = toKey(year, month, day);
    return holidays.some((h: any) => {
      const p = parseYMD(h.date);
      return !!p && toKey(p[0], p[1], p[2]) === target;
    });
  };

  // Is employee ki approved leave us din par hai? (full ya half day)
  const getApprovedLeaveOnDay = (
    employeeId: number,
    day: number,
  ): LeaveRecord | null => {
    const leaves = leaveSummaryMap.get(Number(employeeId))?.leaves || [];
    const target = toKey(year, month, day);
    return (
      leaves.find((l) => {
        const s = parseYMD(l.start_date);
        const e = parseYMD(l.end_date);
        if (!s || !e) return false;
        return (
          toKey(s[0], s[1], s[2]) <= target && target <= toKey(e[0], e[1], e[2])
        );
      }) || null
    );
  };

  // Future date ka status: Holiday > Approved Leave > "-"
  // Weekend (Sat/Sun) par bhi approved leave / holiday dikhegi aur count hogi
  const getFutureStatus = (
    employeeId: number,
    day: number,
  ): AttendanceSymbol => {
    if (isHolidayDate(day)) return "H";

    const leave = getApprovedLeaveOnDay(employeeId, day);
    if (leave) return leave.is_half_day ? "HD" : "L";
    return "-";
  };

  // Colors for status badges
  const getStatusColor = (status: AttendanceSymbol) => {
    switch (status) {
      case "P":
        return "present-status_dv";

      case "A":
        return "absent_blg_dv";

      case "L":
        return "leave_blg_dv";

      case "H":
        return "holidaystatus_blg_dv";

      case "W":
        return "weekend-status_dv";

      case "HD":
        return "halfday_blg_dv";

      case "ED":
        return "extraday-status_dv";

      case "NS":
        return "nightshift-status_dv";

      case "-":
        return "bg-gray-200 text-gray-500";

      default:
        return "";
    }
  };

  // Meeting ka P / A badge (attendance wale badge jaisa hi size)
  const renderMeetingBadge = (m: MeetingItem, isFuture: boolean) => {
    const isPresent = m.status === "Present";
    const cls = isFuture
      ? getStatusColor("-")
      : isPresent
        ? getStatusColor("P")
        : getStatusColor("A");

    return (
      <span
        key={m.meeting_id}
        onClick={() =>
          navigate("/meeting_detail", {
            state: {
              meetingID: m.meeting_id,
              startMeetDate: m.start_datetime,
              endMeetDate: m.end_datetime,
            },
          })
        }
        className={`relative group inline-block w-6 h-6 rounded text-xs leading-6 cursor-pointer ${cls}`}
      >
        {isFuture ? "M" : isPresent ? "P" : "A"}

        {/* Hover tooltip */}
        <span className="hidden group-hover:block absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-1 w-max max-w-[220px] rounded bg-gray-900 text-white text-xs text-left px-2 py-1 shadow-lg whitespace-normal pointer-events-none">
          <span className="block font-semibold">{m.title}</span>
          <span className="block">Time: {m.time}</span>
          <span className="block">
            Status: {isFuture ? "Upcoming" : m.status}
          </span>
        </span>
      </span>
    );
  };

  // W / H / - badge (meeting ke bina din ke liye)
  const renderPlainBadge = (status: AttendanceSymbol) => (
    <span
      className={`inline-block w-6 h-6 rounded text-xs leading-6 ${getStatusColor(status)}`}
    >
      {status}
    </span>
  );

  // Handle row selection
  const handleRowClick = (employeeId: number) => {
    setSelectedEmployeeId(
      selectedEmployeeId === employeeId ? null : employeeId,
    );
  };

  // Calculate working minutes from a single attendance record
  const calculateWorkingMinutes = (record: AttendanceRecord): number => {
    if (!record.is_present || !record.check_in_time || !record.check_out_time) {
      return 0;
    }

    // If hours and minutes are provided directly
    if (record.hours !== undefined && record.minutes !== undefined) {
      return record.hours * 60 + record.minutes;
    }

    // Calculate from check-in and check-out times (they come as full datetime strings)
    try {
      const checkIn = new Date(record.check_in_time);
      const checkOut = new Date(record.check_out_time);

      if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) {
        return 0;
      }

      const diffMs = checkOut.getTime() - checkIn.getTime();
      return Math.floor(diffMs / (1000 * 60));
    } catch (error) {
      return 0;
    }
  };

  // Convert minutes to hours and minutes string
  const formatWorkingHours = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  // Aggregate multiple records for the same date
  const aggregateAttendanceByDate = (
    attendance: AttendanceRecord[],
  ): AggregatedDayRecord[] => {
    const dateMap = new Map<string, AggregatedDayRecord>();

    attendance.forEach((record) => {
      const dateKey = record.record_date;

      if (!dateMap.has(dateKey)) {
        // Create new aggregated record
        dateMap.set(dateKey, {
          record_date: record.record_date,
          week_day: record.week_day,
          is_present: false,
          is_leave: record.is_leave,
          is_holiday: record.is_holiday,
          is_absent: true, // Default to absent, will be updated if any present record found
          is_weekend: record.is_weekend,
          is_extra_day: record.is_extra_day ?? false,
          is_night_shift: record.is_night_shift ?? false,
          is_half_day: record.is_half_day ?? false,
          total_minutes: 0,
          records: [],
        });
      }

      const aggregated = dateMap.get(dateKey)!;
      aggregated.records.push(record);

      // If any record shows present, mark the day as present
      if (record.is_present) {
        aggregated.is_present = true;
        aggregated.is_absent = false;
        aggregated.total_minutes += calculateWorkingMinutes(record);
      }

      // Update other flags (take the most recent or combine logically)
      aggregated.is_leave = aggregated.is_leave || record.is_leave;
      aggregated.is_holiday = aggregated.is_holiday || record.is_holiday;
      aggregated.is_weekend = aggregated.is_weekend || record.is_weekend;
      aggregated.is_extra_day =
        aggregated.is_extra_day || (record.is_extra_day ?? false);

      aggregated.is_night_shift =
        aggregated.is_night_shift || (record.is_night_shift ?? false);

      aggregated.is_half_day =
        aggregated.is_half_day || (record.is_half_day ?? false);
    });

    return Array.from(dateMap.values());
  };

  // Calculate total working hours for an employee from aggregated records
  const calculateTotalWorkingHours = (
    aggregatedRecords: AggregatedDayRecord[],
  ): string => {
    const totalMinutes = aggregatedRecords.reduce((sum, record) => {
      return sum + record.total_minutes;
    }, 0);

    return formatWorkingHours(totalMinutes);
  };

  const handleExportAttendance = async () => {
    if (!agencyID || !month || !year) return;

    const firstDayOfMonth = startOfMonth(new Date(year, month - 1));
    const lastDayOfMonth = endOfMonth(new Date(year, month - 1));

    const formattedStartDate = format(firstDayOfMonth, "yyyy-MM-dd");
    const formattedEndDate = format(lastDayOfMonth, "yyyy-MM-dd");

    try {
      const result = await exportAttendance({
        variables: {
          input: {
            agency_id: parseInt(agencyID),
            start_date: formattedStartDate,
            end_date: formattedEndDate,
          },
        },
      });

      if (result.data?.export_attendance_to_excel?.status_code === 200) {
        const fileUrl = result.data.export_attendance_to_excel.file_url;
        const filename = result.data.export_attendance_to_excel.filename;

        // Create a temporary link to download the file
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = filename || "attendance_export.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error(
          "Export failed:",
          result.data?.export_attendance_to_excel?.message,
        );
      }
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  const filteredEmployees = employees.filter((emp) =>
    emp.employee_name?.toLowerCase().includes(searchTerm.trim().toLowerCase()),
  );

  // ---------- Meetings grouping (pod / meeting type rows) ----------

  // Row order: Morning -> Pods -> Evening -> baaki meeting types
  const getGroupOrder = (g: MeetingGroup): number => {
    const name = g.label.toLowerCase();
    if (name.includes("morning")) return 0;
    if (g.isPod) return 1;
    if (name.includes("evening")) return 2;
    return 3;
  };

  // employee_id -> [{ pod_id, pod_name }] (pod ke members se)
  const podsByEmployee = new Map<
    number,
    { pod_id: number; pod_name: string }[]
  >();
  (
    podsData?.pods_data?.data.filter((it: any) => it.pod_name != "All Pods") ||
    []
  ).forEach((pod: any) => {
    (pod.members || []).forEach((mem: any) => {
      const empId = Number(mem.employee_id);
      const list = podsByEmployee.get(empId) || [];
      if (!list.some((p) => p.pod_id === pod.pod_id)) {
        list.push({ pod_id: pod.pod_id, pod_name: pod.pod_name });
      }
      podsByEmployee.set(empId, list);
    });
  });

  const employeeActiveStatsMap = new Map<
    number,
    {
      active_goal_count: number;
      active_task_count: number;
      groups: MeetingGroup[];
      podCount: number;
      presentTotal: number;
      absentTotal: number;
      upcomingTotal: number;
    }
  >();

  activeStatsData?.employee_active_stats?.records?.forEach((record: any) => {
    const groupMap = new Map<string, MeetingGroup>();
    let presentTotal = 0;
    let absentTotal = 0;
    let upcomingTotal = 0;

    (record.meeting_details || []).forEach((d: DailyMeeting) => {
      if (!d.meetings?.length) return;
      const day = Number(d.date.split("-")[2]); // timezone issue se bachne ke liye manual parse

      d.meetings.forEach((m) => {
        const isPod = m.pod_id !== null && m.pod_id !== undefined;

        if (!isPod && m.meeting_type_name?.toLowerCase() === "pod meeting")
          return;

        const key = isPod ? `pod-${m.pod_id}` : `type-${m.meeting_type_name}`;

        const label = isPod
          ? m.pod_name || `Pod ${m.pod_id}`
          : m.meeting_type_name || "Other Meeting";

        if (!groupMap.has(key)) {
          groupMap.set(key, { key, label, isPod, byDay: new Map() });
        }
        const group = groupMap.get(key)!;
        group.byDay.set(day, [...(group.byDay.get(day) || []), m]);

        if (isFutureDate(day)) upcomingTotal++;
        else if (m.status === "Present") presentTotal++;
        else absentTotal++;
      });
    });

    // Jis pod ka employee member hai wo row hamesha dikhao (meeting na ho tab bhi)
    (podsByEmployee.get(Number(record.employee_id)) || []).forEach((p) => {
      const key = `pod-${p.pod_id}`;
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          key,
          label: p.pod_name,
          isPod: true,
          byDay: new Map(),
        });
      }
    });

    const groups = Array.from(groupMap.values()).sort((a, b) => {
      const diff = getGroupOrder(a) - getGroupOrder(b);
      return diff !== 0 ? diff : a.label.localeCompare(b.label);
    });

    employeeActiveStatsMap.set(Number(record.employee_id), {
      active_goal_count: record.active_goal_count || 0,
      active_task_count: record.active_task_count || 0,
      groups,
      podCount: groups.filter((g) => g.isPod).length,
      presentTotal,
      absentTotal,
      upcomingTotal,
    });
  });

  // Employees jinka active-stats record nahi aaya, par pod member hain
  podsByEmployee.forEach((pods, empId) => {
    if (employeeActiveStatsMap.has(empId)) return;
    const groups: MeetingGroup[] = pods
      .map((p) => ({
        key: `pod-${p.pod_id}`,
        label: p.pod_name,
        isPod: true,
        byDay: new Map<number, MeetingItem[]>(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    employeeActiveStatsMap.set(empId, {
      active_goal_count: 0,
      active_task_count: 0,
      groups,
      podCount: groups.length,
      presentTotal: 0,
      absentTotal: 0,
      upcomingTotal: 0,
    });
  });

  const attendanceStatuses = [
    {
      status: "P",
      label: "Present",
      className: "present-status_dv",
    },
    {
      status: "A",
      label: "Absent",
      className: "absent_blg_dv",
    },
    {
      status: "L",
      label: "Leave",
      className: "leave_blg_dv",
    },
    {
      status: "HD",
      label: "Half Day",
      className: "halfday_blg_dv",
    },
    {
      status: "W",
      label: "Weekend",
      className: "weekend-status_dv",
    },
    {
      status: "H",
      label: "Holiday",
      className: "holidaystatus_blg_dv",
    },
    {
      status: "ED",
      label: "Extra Day",
      className: "extraday-status_dv",
    },
    {
      status: "NS",
      label: "Night Shift",
      className: "nightshift-status_dv",
    },
  ];

  return (
    <>
      <div
        id="cts-respon-p-dv"
        className="w-full flex flex-col items-start justify-start py-40 px-20  mq575:px-5 box-border gap-[20px] max-w-[100%] mq1425:max-w-full"
      >
        <div className="min-w-full flex flex-col items-start justify-start gap-6">
          <BreadCumb />

          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => navigate(-1)}
            />
            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
              Attendance
            </h3>
          </div>
          <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
            <div className="grid gap-[24px] w-full">
              <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full mt-[8px] items-end">
                <div className="grid">
                  <label className="self-stretch relative font-semibold pb-2 dark:text-white">
                    Select Month
                  </label>
                  <select
                    value={month}
                    onChange={(e) => setMonth(Number(e.target.value))}
                    className="border rounded p-2 select-dropdown-ui border-surface-border-blg-dv dark:bg-slate-700"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>
                        {new Date(0, m - 1).toLocaleString("default", {
                          month: "long",
                        })}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid">
                  <label className="self-stretch relative font-semibold pb-2 dark:text-white">
                    Select Years
                  </label>
                  <select
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    className="border rounded p-2 select-dropdown-ui border-surface-border-blg-dv dark:bg-slate-700"
                  >
                    {Array.from(
                      { length: 5 },
                      (_, i) => new Date().getFullYear() - 2 + i,
                    ).map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handleExportAttendance}
                  disabled={exportLoading || !employees.length}
                  className="export-btn new-export-btn cursor-pointer disabled:bg-gray-400 w-fit text-white px-4 py-2 rounded flex items-center gap-2 transition-colors"
                >
                  {exportLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      Export to Excel
                    </>
                  )}
                </button>
              </div>
              <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-1 mq575:grid-cols-1 mt-[8px]  min-w-full self-stretch flex-row items-start justify-start gap-[8px] mq991:gap-[8px] text-lg">
                {holidays?.length > 0 && (
                  <div className="h-full flex flex-col items-center resource_box_item lightblue-item px-4 py-2 ">
                    <div className="h-full flex flex-col items-center w-full bg-white p-2">
                      <div className="h-full self-stretch rounded-num-20 overflow-hidden flex items-center justify-between gap-2">
                        <div className="block gap-0 w-full">
                          <div className="w-full justify-between flex ">
                            <h4 className="flex-1 flex items-center my-0">
                              Holiday
                            </h4>
                            <h2 className="text-left flex items-center my-0">
                              {holidays.length}
                            </h2>
                          </div>

                          <div className="grid grid-cols-2 mq1400:grid-2 mq1275:grid-cols-2 mq950:grid-cols-1 mq575:grid-cols-1 mt-[8px] min-w-full self-stretch flex-row items-start justify-start gap-[8px] mq991:gap-[8px] text-lg">
                            {holidays.length > 0 ? (
                              holidays.map((holiday: any) => (
                                <div
                                  key={holiday.id}
                                  className="border-[1px] border-solid border-surface-border p-1 rounded-radius-md  relative"
                                >
                                  <h5 className="flex-1 flex items-center my-0">
                                    {holiday.name}
                                  </h5>

                                  <p className="text-xs dark:text-white my-0 mt-2">
                                    {format(
                                      new Date(holiday.date),
                                      "MMMM d, yyyy",
                                    )}
                                  </p>
                                </div>
                              ))
                            ) : (
                              <div>
                                <p className="text-xs dark:text-white my-0 mt-2">
                                  No holidays
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="allattendce_status_main grid gap-[8px] mt-9">
                <div className="grid grid-cols-9 mq1400:grid-9 mq1275:grid-cols-6 mq950:grid-cols-2 mq575::grid-cols-2  gap-[8px] w-full">
                  {attendanceStatuses.map(({ status, label, className }) => (
                    <div
                      key={status}
                      className="present_blg_dv flex items-center gap-[8px]"
                    >
                      <span
                        className={`${className} flex justify-center items-center`}
                      >
                        {status}
                      </span>

                      <p className="text-sm dark:text-white my-0 text-white">
                        {label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-1 gap-[8px] min-w-full mb-6">
                <div className="grid gap-0">
                  <label className="self-stretch relative font-semibold pb-2 dark:text-white">
                    Search Employee
                  </label>
                  <div className="relative main-emp-search-attendence">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search employee..."
                      className=" rounded p-2 w-full box-border border-surface-border-blg-dv dark:bg-slate-700  emp-search-attendence py-2.5 px-[15px] gap-[8px] border-[1px] border-solid border-surface-border mq450:flex-nowrap"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 bg-transparent border-none cursor-pointer"
                        aria-label="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {filteredEmployees?.length > 0 ? (
                <div className="flex w-full overflow-x-auto relative  main-new-attendancetbl-blg-dv">
                  <table
                    className="attendancetbl-blg-dv new-attendancetbl-blg-dv"
                    cellSpacing={0}
                    cellPadding={0}
                  >
                    <thead className="sticky top-0 z-20 bg-white">
                      <tr>
                        <th
                          className="sticky left-0 bg-white z-30 border-r text-center"
                          style={{ width: "40px", minWidth: "40px" }}
                        >
                          <b>S.N.</b>
                        </th>
                        <th className="sticky left-0 bg-white z-30 border-r">
                          <b>Employee</b>
                        </th>
                        {Array.from({ length: daysInMonth }, (_, i) => {
                          const date = new Date(year, month - 1, i + 1);
                          const dayName = date.toLocaleString("default", {
                            weekday: "short",
                          }); // Mon, Tue, etc.
                          return (
                            <th
                              key={i + 1}
                              className="p-2 border text-center bg-white"
                            >
                              <div className="flex flex-col items-center">
                                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                  {dayName}
                                </span>
                                <span className="font-semibold">{i + 1}</span>
                              </div>
                            </th>
                          );
                        })}
                        <th className="bg-whitev attendancesummary-dv">
                          <b>Attendance Summary</b>
                        </th>
                        <th className="bg-white">
                          <b>Work status</b>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmployees?.map(
                        (emp: EmployeeRecord, index: number) => {
                          // Aggregate attendance records by date (sum multiple records per day)
                          const aggregatedAttendance =
                            aggregateAttendanceByDate(emp.attendance);

                          // Build day map using aggregated records
                          const dayMap = new Map<number, AggregatedDayRecord>();
                          aggregatedAttendance.forEach((rec) => {
                            const day = new Date(rec.record_date).getDate();
                            dayMap.set(day, rec);
                          });

                          // ---------- Future dates: sirf Holiday / Approved Leave count hote hain ----------
                          // Weekend par bhi jo L / HD / H dikhta hai wahi count hota hai
                          let futureHolidayCount = 0;
                          let futureLeaveCount = 0;
                          let futureHalfDayCount = 0;

                          for (let d = 1; d <= daysInMonth; d++) {
                            if (!isFutureDate(d)) continue;
                            const s = getFutureStatus(emp.employee_id, d);
                            if (s === "H") futureHolidayCount += 1;
                            else if (s === "L") futureLeaveCount += 1;
                            else if (s === "HD") futureHalfDayCount += 0.5;
                          }

                          // Count present days, absent days, and leave days (excluding future dates)
                          const presentCount = aggregatedAttendance.filter(
                            (a) => {
                              const recordDay = new Date(
                                a.record_date,
                              ).getDate();
                              const isNotFuture = !isFutureDate(recordDay);

                              return (
                                a.is_present &&
                                !a.is_half_day &&
                                !a.is_extra_day &&
                                !a.is_night_shift &&
                                isNotFuture
                              );
                            },
                          ).length;

                          const absentCount = aggregatedAttendance.filter(
                            (a) => {
                              const recordDay = new Date(
                                a.record_date,
                              ).getDate();
                              const isNotFuture = !isFutureDate(recordDay);

                              return (
                                a.is_absent &&
                                !a.is_weekend &&
                                !a.is_holiday &&
                                !a.is_leave &&
                                !a.is_half_day &&
                                isNotFuture
                              );
                            },
                          ).length;

                          // Leave = past leave + future approved leave
                          const leaveCount =
                            aggregatedAttendance.filter((a) => {
                              const recordDay = new Date(
                                a.record_date,
                              ).getDate();
                              const isNotFuture = !isFutureDate(recordDay);

                              return (
                                a.is_leave &&
                                !a.is_present &&
                                !a.is_half_day &&
                                isNotFuture
                              );
                            }).length + futureLeaveCount;

                          // Half Day = 0.5 (past + future approved half day)
                          const halfDayCount =
                            aggregatedAttendance.reduce((count, a) => {
                              const recordDay = new Date(
                                a.record_date,
                              ).getDate();

                              if (a.is_half_day && !isFutureDate(recordDay)) {
                                return count + 0.5;
                              }

                              return count;
                            }, 0) + futureHalfDayCount;

                          const weekendCount = aggregatedAttendance.filter(
                            (a) => {
                              const recordDay = new Date(
                                a.record_date,
                              ).getDate();
                              return a.is_weekend && !isFutureDate(recordDay);
                            },
                          ).length;

                          // Holiday = past holiday + future holiday
                          const holidayCount =
                            aggregatedAttendance.filter((a) => {
                              const recordDay = new Date(
                                a.record_date,
                              ).getDate();
                              return a.is_holiday && !isFutureDate(recordDay);
                            }).length + futureHolidayCount;

                          const extraDayCount = aggregatedAttendance.filter(
                            (a) => {
                              const recordDay = new Date(
                                a.record_date,
                              ).getDate();
                              return a.is_extra_day && !isFutureDate(recordDay);
                            },
                          ).length;

                          const nightShiftCount = aggregatedAttendance.filter(
                            (a) => {
                              const recordDay = new Date(
                                a.record_date,
                              ).getDate();
                              return (
                                a.is_night_shift && !isFutureDate(recordDay)
                              );
                            },
                          ).length;

                          const leaveInfo = leaveSummaryMap.get(emp.employee_id);
                          const employeeLeaves = leaveInfo?.leaves || [];

                          const startMonth = getLeaveStartMonth(
                            leaveInfo?.date_of_joining,
                          );

                          const previousLeaveBalance = getPreviousLeaveBalance(
                            employeeLeaves,
                            startMonth,
                          );

                          const thisMonthCredit =
                            month >= startMonth ? LEAVE_PER_MONTH : 0;

                          const currentMonthAvailable =
                            previousLeaveBalance + thisMonthCredit;

                          // Actual leave pay type ke basis par calculation
                          const monthLeaveBreakdown = getLeaveBreakdownInMonth(
                            employeeLeaves,
                            year,
                            month,
                          );

                          const paidLeaveTotal = monthLeaveBreakdown.paid;
                          const unpaidLeaveTotal = monthLeaveBreakdown.unpaid;

                          // Payout text ke liye: paid full leave aur paid half day alag
                          const paidFullLeave = monthLeaveBreakdown.paidFull;
                          const paidHalfLeave = monthLeaveBreakdown.paidHalf;

                          // "1 Half Day (0.5 + 0.5)" jaisa breakdown dikhane ke liye
                          const halfDayEntries = Math.round(halfDayCount / 0.5);
                          const halfDayBreakdown =
                            halfDayEntries > 0
                              ? `(${Array(halfDayEntries).fill("0.5").join(" + ")})`
                              : "";

                          // Available balance se sirf paid leave deduct hogi
                          const remainingLeaveBalance = Math.max(
                            0,
                            currentMonthAvailable - paidLeaveTotal,
                          );

                          console.log("Leave Balance Debug:", {
                            joiningDate: leaveInfo?.date_of_joining,
                            startMonth,
                            previousLeaveBalance,
                            thisMonthCredit,
                            currentMonthAvailable,
                            leaveCount,
                            halfDayCount,
                            paidLeaveTotal,
                            remainingLeaveBalance,
                            leaves: leaveInfo?.leaves,
                          });

                          const workingHalfDayCount =
                            aggregatedAttendance.filter((a) => {
                              const recordDay = new Date(
                                a.record_date,
                              ).getDate();

                              // Half day wale har past din me aadha din kaam hua maana jayega
                              return a.is_half_day && !isFutureDate(recordDay);
                            }).length * 0.5;

                          const payoutDays =
                            presentCount +
                            workingHalfDayCount +
                            nightShiftCount +
                            holidayCount +
                            extraDayCount +
                            paidLeaveTotal;

                          const isSelected =
                            selectedEmployeeId === emp.employee_id;

                          const activeStats = employeeActiveStatsMap.get(
                            emp.employee_id,
                          );

                          // Main row + meeting rows (summary columns itne rows me phailenge)
                          const blockRowSpan =
                            1 + (activeStats?.groups.length ?? 0);

                          return (
                            <Fragment key={emp.employee_id}>
                              <tr
                                className={`${isSelected ? "bg-blue-50 dark:bg-blue-900/20" : ""} hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors detawise-td-dv`}
                              >
                                <td
                                  className={`sticky left-0 ${
                                    isSelected
                                      ? "bg-blue-50 dark:bg-blue-900/20"
                                      : "bg-white"
                                  } z-10 border-r text-center align-top`}
                                  style={{ width: "40px", minWidth: "40px" }}
                                >
                                  {index + 1}
                                </td>

                                <td
                                  className={`sticky ${
                                    isSelected
                                      ? "bg-blue-50 dark:bg-blue-900/20"
                                      : "bg-white"
                                  } z-10 border-r cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-800/30 transition-colors align-top`}
                                  style={{ left: "33px" }}
                                  onClick={() =>
                                    handleRowClick(emp.employee_id)
                                  }
                                >
                                  <div>{emp.employee_name}</div>

                                  {(activeStats?.podCount ?? 0) > 0 && (
                                    <div className="text-[11px] font-bold mt-1">
                                      In {activeStats?.podCount} POD
                                    </div>
                                  )}

                                  {(activeStats?.groups.length ?? 0) > 0 && (
                                    <div className="mt-3">
                                      <div className="text-[11px] dark:text-white">
                                        Total Meeting summary
                                      </div>
                                      <div className="flex gap-[4px] mt-1 text-[10px] text-white font-semibold">
                                        <span className="px-1.5 py-0.5 rounded bg-teal-500">
                                          {activeStats?.presentTotal} P
                                        </span>
                                        <span className="px-1.5 py-0.5 rounded bg-teal-500 absent_status-blog">
                                          {activeStats?.absentTotal} A
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </td>

                                {Array.from({ length: daysInMonth }, (_, i) => {
                                  const dayNumber = i + 1;
                                  const record = dayMap.get(dayNumber);

                                  // Check if this is a future date
                                  const isFuture = isFutureDate(dayNumber);

                                  // Future date: Holiday / Approved Leave ho to wahi dikhao, warna "-"
                                  const status: AttendanceSymbol = isFuture
                                    ? getFutureStatus(
                                        emp.employee_id,
                                        dayNumber,
                                      )
                                    : record
                                      ? getStatus(record)
                                      : "A";
                                  const workingHours =
                                    record && status === "P"
                                      ? formatWorkingHours(record.total_minutes)
                                      : "";

                                  return (
                                    <td
                                      key={dayNumber}
                                      className="p-1 border text-center align-top"
                                    >
                                      <span
                                        className={`inline-block w-6 h-6 rounded ${getStatusColor(status)} text-xs leading-6 ${status !== "-" ? "cursor-pointer" : ""}`}
                                        title={
                                          status === "P" && workingHours
                                            ? `Working Hours: ${workingHours}`
                                            : isFuture
                                              ? status === "-"
                                                ? "Future date"
                                                : status === "H"
                                                  ? "Holiday"
                                                  : "Approved leave"
                                              : ""
                                        }
                                      >
                                        {status}
                                      </span>
                                    </td>
                                  );
                                })}

                                <td
                                  rowSpan={blockRowSpan}
                                  className="p-2 leavesummary-td-item align-top"
                                >
                                  <div className="flex flex-col gap-[4px]">
                                    <ul className="flex gap-[8px] items-center leavesummary-blog">
                                      <li className="leavesummaryinner flex gap-[4px]">
                                        <span className="summary-status-green">
                                          {presentCount}
                                        </span>
                                        <span className="text-xs dark:text-white">
                                          Present
                                        </span>
                                      </li>
                                      <li className="leavesummaryinner flex gap-[4px]">
                                        <span className="summary-status-red">
                                          {absentCount}
                                        </span>
                                        <span className="text-xs dark:text-white">
                                          Absent
                                        </span>
                                      </li>
                                      <li className="leavesummaryinner flex gap-[4px]">
                                        <span className="leave-status-yellow">
                                          {leaveCount}
                                        </span>
                                        <span className="text-xs dark:text-white">
                                          Leave
                                        </span>
                                      </li>
                                      <li className="leavesummaryinner flex gap-[4px]">
                                        <span className="haldday-status-aten">
                                          {fmt(halfDayCount)}
                                        </span>
                                        <span className="text-xs dark:text-white">
                                          Half Day {halfDayBreakdown}
                                        </span>
                                      </li>
                                      <li className="leavesummaryinner flex gap-[4px]">
                                        <span className="weekend-status-aten">
                                          {weekendCount}
                                        </span>
                                        <span className="text-xs dark:text-white">
                                          Weekend
                                        </span>
                                      </li>
                                      <li className="leavesummaryinner flex gap-[4px]">
                                        <span className="holiday-status-aten">
                                          {holidayCount}
                                        </span>
                                        <span className="text-xs dark:text-white">
                                          Holiday
                                        </span>
                                      </li>
                                      <li className="leavesummaryinner flex gap-[4px]">
                                        <span className="extraday-status-aten">
                                          {extraDayCount}
                                        </span>
                                        <span className="text-xs dark:text-white">
                                          Extra Day
                                        </span>
                                      </li>
                                      <li className="leavesummaryinner flex gap-[4px]">
                                        <span className="nightshift-status-aten">
                                          {nightShiftCount}
                                        </span>
                                        <span className="text-xs dark:text-white">
                                          Night Shift
                                        </span>
                                      </li>
                                    </ul>
                                    <div className="grid gap-2">
                                      <label className="self-stretch text-xs relative font-semibold dark:text-white">
                                        Leave Status
                                      </label>
                                      <ul className="flex gap-[8px] items-center leavesummary-blog flex-wrap">
                                        <li className="leavesummaryinner flex gap-[4px]">
                                          <span className="summary-status-green">
                                            {fmt(previousLeaveBalance)}
                                          </span>
                                          <span className="text-xs dark:text-white">
                                            Previous Balance
                                          </span>
                                        </li>
                                        <li className="leavesummaryinner flex gap-[4px]">
                                          <span className="leave-status-yellow">
                                            {fmt(thisMonthCredit)}
                                          </span>
                                          <span className="text-xs dark:text-white">
                                            This Month
                                          </span>
                                        </li>
                                        <li className="leavesummaryinner flex gap-[4px]">
                                          <span className="summary-status-red">
                                            {fmt(paidLeaveTotal)}
                                          </span>
                                          <span className="text-xs dark:text-white">
                                            Paid Leave Used
                                          </span>
                                        </li>
                                        <li className="leavesummaryinner flex gap-[4px]">
                                          <span className="summary-status-red">
                                            {fmt(unpaidLeaveTotal)}
                                          </span>
                                          <span className="text-xs dark:text-white">
                                            Unpaid Leave
                                          </span>
                                        </li>
                                        <li className="leavesummaryinner flex gap-[4px]">
                                          <span className="summary-status-green">
                                            {fmt(remainingLeaveBalance)}
                                          </span>
                                          <span className="text-xs dark:text-white">
                                            Available
                                          </span>
                                        </li>
                                      </ul>
                                    </div>
                                    <div className="grid gap-2">
                                      <label className="self-stretch text-xs relative font-semibold dark:text-white">
                                        Payout Days
                                      </label>
                                      <ul className="flex gap-[8px] items-center leavesummary-blog">
                                        <li className="leavesummaryinner flex gap-[4px]">
                                          <span className="nightshift-status-aten">
                                            {fmt(payoutDays)}
                                          </span>
                                          <span className="text-xs dark:text-white">
                                            {fmt(payoutDays)} days (
                                            {presentCount} Present +{" "}
                                            {nightShiftCount} Night Shift +{" "}
                                            {holidayCount} Holiday +{" "}
                                            {extraDayCount} Extra Day +{" "}
                                            {fmt(paidFullLeave)} Paid Leave +{" "}
                                            {fmt(paidHalfLeave)} Paid Half Day
                                            {workingHalfDayCount > 0 &&
                                              ` + ${fmt(workingHalfDayCount)} Worked Half Day`}
                                            )
                                          </span>
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                </td>
                                <td
                                  rowSpan={blockRowSpan}
                                  className="p-2 align-top"
                                >
                                  {activeStatsLoading ? (
                                    <div>Loading</div>
                                  ) : (
                                    <>
                                      <div className="flex flex-col gap-[4px] taskstatus-td">
                                        <span className="text-sm dark:text-white">
                                          Open Goals:
                                          <span className="font-semibold">
                                            {activeStats?.active_goal_count ??
                                              0}
                                          </span>
                                        </span>

                                        <span className="text-sm dark:text-white">
                                          Open Tasks:
                                          <span className="font-semibold">
                                            {" "}
                                            {activeStats?.active_task_count ??
                                              0}
                                          </span>
                                        </span>
                                      </div>
                                    </>
                                  )}
                                </td>
                              </tr>

                              {/* Pod / meeting type rows */}
                              {(activeStats?.groups || []).map((group) => (
                                <tr
                                  key={`${emp.employee_id}-${group.key}`}
                                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                                >
                                  {/* S.N. column - blank for pod/meeting rows */}
                                  <td
                                    className="sticky left-0 bg-white z-10 border-r"
                                    style={{ width: "40px", minWidth: "40px" }}
                                  />

                                  {/* Employee / Pod / Meeting column */}
                                  <td
                                    className="sticky bg-white z-10 border-r leading-tight px-2 py-1"
                                    style={{ left: "33px" }}
                                  >
                                    <div className="text-left text-[11px] dark:text-white">
                                      {group.label}
                                    </div>
                                  </td>

                                  {Array.from({ length: daysInMonth }, (_, i) => {
                                    const dayNumber = i + 1;
                                    const isFuture = isFutureDate(dayNumber);
                                    const items =
                                      group.byDay.get(dayNumber) || [];
                                    const dayRecord = dayMap.get(dayNumber);

                                    let content: ReactNode = null;
                                    if (items.length > 0) {
                                      content = (
                                        <div className="flex flex-col items-center gap-[4px]">
                                          {items.map((m) =>
                                            renderMeetingBadge(m, isFuture),
                                          )}
                                        </div>
                                      );
                                    } else if (isFuture) {
                                      content = renderPlainBadge(
                                        isHolidayDate(dayNumber) ? "H" : "-",
                                      );
                                    } else if (dayRecord?.is_holiday) {
                                      content = renderPlainBadge("H");
                                    } else if (dayRecord?.is_weekend) {
                                      content = renderPlainBadge("W");
                                    }

                                    return (
                                      <td
                                        key={dayNumber}
                                        className="p-1 border text-center"
                                      >
                                        {content}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </Fragment>
                          );
                        },
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div>No records found.</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {(loading || exportLoading) && <Loader />}
    </>
  );
};

export default AllAttendanceNew;